import type {
  SearchBarContextProps,
  SuggestionSelectionProps
} from '@carvana/merch-search-bar';
import { useCallback, useContext, useMemo } from 'react';
import { StickySearchContext } from '@/srp/Search/components/StickySearchProvider';
import { apikUrl, assetsCdnUrl } from '~/constants/environment';
import SearchSource from '~/constants/srp/SearchSource';
import { useIsMobile } from '~/context/common/DeviceContext';
import { useUserLocation } from '~/context/common/UserContext';
import AppliedFiltersContext from '~/context/srp/AppliedFiltersContext';
import ModalContext from '~/context/srp/ModalContext';
import { useSavedSearches } from '~/context/srp/SavedSearchesContext';
import { useTrackEvent } from '~/context/srp/SearchAnalyticsContext';
import SearchEventsDataContext from '~/context/srp/SearchEventsDataContext';
import decodeCvnaId from '~/utilities/srp/cvnaId';

export default function useSearchBarProps(): SearchBarContextProps {
  const { setAppliedFilters } = useContext(AppliedFiltersContext);
  const { setIsSearchBarFocused, setSearchBarModalOpen } =
    useContext(ModalContext);
  const { setSearchSource, setKeywordSuggestion } = useContext(
    SearchEventsDataContext
  );
  const { isStickySearchBar } = useContext(StickySearchContext);
  const showModal = useIsMobile();
  const savedSearches = useSavedSearches();
  const trackEvent = useTrackEvent();
  const { state = '' } = useUserLocation();

  const onCloseModal = useCallback(() => {
    setSearchBarModalOpen(false);
  }, [setSearchBarModalOpen]);

  const onShowModalClick = useCallback(() => {
    setSearchBarModalOpen(true);
  }, [setSearchBarModalOpen]);

  const onSuggestionSelection = useCallback(
    ({ cvnaId, eventData }: SuggestionSelectionProps) => {
      const searchSource = SearchSource[eventData.textSource ?? ''];
      if (searchSource) {
        setSearchSource(searchSource);
      }
      setKeywordSuggestion({
        ...eventData,
        isStickySearchbar: isStickySearchBar
      });
      setAppliedFilters(decodeCvnaId(cvnaId)?.filters ?? {});
    },
    [
      isStickySearchBar,
      setAppliedFilters,
      setKeywordSuggestion,
      setSearchSource
    ]
  );

  return useMemo(
    () => ({
      apiBaseUrl: apikUrl,
      cdnBaseUrl: assetsCdnUrl,
      geolocationState: state,
      inputBgColor: 'gray',
      onCloseModal,
      onFocus: setIsSearchBarFocused,
      onShowModalClick,
      onSuggestionSelection,
      refSource: 'srp',
      savedSearches,
      showButton: false,
      showModal,
      trackEvent
    }),
    [
      onCloseModal,
      onShowModalClick,
      onSuggestionSelection,
      savedSearches,
      setIsSearchBarFocused,
      showModal,
      state,
      trackEvent
    ]
  );
}
